import type { EscopoDeContratos, SituacaoDeContrato } from '@obras/shared';
import { SITUACOES_DE_CONTRATO } from '@obras/shared';
import { Building2, Files, ScrollText, SearchX, UserRound } from 'lucide-react';
import { useMemo, useState } from 'react';

import { PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { EmptyState, ErrorState, RefreshingBar } from '../components/ui/Feedback.js';
import { SearchInput } from '../components/ui/Field.js';
import { Segmented, type SegmentedOption } from '../components/ui/Segmented.js';
import { Tab, TabList } from '../components/ui/Tabs.js';
import {
  APARENCIA_DA_SITUACAO,
  PlanilhaDeContratos,
  PlanilhaDeContratosEsqueleto,
} from '../features/contratos/PlanilhaDeContratos.js';
import {
  COLUNAS_DA_PLANILHA,
  contarPorSituacao,
  ORDEM_PADRAO,
  ordenar,
  SENTIDO_INICIAL,
  textoDeBusca,
  type ColunaDaPlanilha,
  type Ordem,
} from '../features/contratos/planilha.js';
import { useContratos } from '../features/obras/api.js';
import { useAuth } from '../lib/auth.js';
import { useStoredState } from '../lib/storage.js';
import { normalizarBusca } from '../lib/texto.js';

const ESCOPOS: readonly SegmentedOption<EscopoDeContratos>[] = [
  { value: 'minhas', label: 'Minhas obras', icon: UserRound },
  { value: 'orgao', label: 'Todo o órgão', icon: Building2 },
];

/** O rótulo da aba, no plural: a aba conta contratos. */
const ABA: Record<SituacaoDeContrato, string> = {
  vencido: 'Vencidos',
  a_vencer: 'A vencer',
  vigente: 'Vigentes',
  sem_vigencia: 'Sem vigência',
  encerrado: 'Encerrados',
};

type FiltroDeSituacao = SituacaoDeContrato | 'todos';

interface EstadoDaPlanilha {
  readonly escopo: EscopoDeContratos;
  readonly situacao: FiltroDeSituacao;
  readonly ordem: Ordem;
}

const INICIAL: EstadoDaPlanilha = { escopo: 'minhas', situacao: 'todos', ordem: ORDEM_PADRAO };

/** O que ficou guardado pode ser de outra versão: o que não se reconhece volta ao padrão. */
function restaurar(bruto: unknown): EstadoDaPlanilha | undefined {
  if (!bruto || typeof bruto !== 'object') return undefined;
  const r = bruto as Record<string, unknown>;
  const ordem = (r['ordem'] ?? {}) as Record<string, unknown>;

  return {
    escopo: r['escopo'] === 'orgao' ? 'orgao' : 'minhas',
    situacao: SITUACOES_DE_CONTRATO.includes(r['situacao'] as SituacaoDeContrato)
      ? (r['situacao'] as SituacaoDeContrato)
      : 'todos',
    ordem: COLUNAS_DA_PLANILHA.includes(ordem['coluna'] as ColunaDaPlanilha)
      ? {
          coluna: ordem['coluna'] as ColunaDaPlanilha,
          sentido: ordem['sentido'] === 'desc' ? 'desc' : 'asc',
        }
      : ORDEM_PADRAO,
  };
}

/**
 * Contratos: o acompanhamento dos contratos das obras (design.md §8, Contratos).
 *
 * Uma planilha, e não um cadastro: cada linha é o contrato de uma obra, com o
 * que já foi preenchido na obra e nos relatórios — número, contratada, valor,
 * saldo, vigência — e a situação de hoje: vencido, a vencer, vigente. Corrige-se
 * um contrato na obra dele, nunca aqui.
 *
 * Abre nas obras de quem está olhando, e "Todo o órgão" mostra o resto. Escopo,
 * aba e ordem ficam guardados no navegador, por pessoa e órgão; a busca, não —
 * voltar à página com um termo esquecido no campo esconderia contratos.
 */
export function ContratosPage() {
  const { user, organization } = useAuth();
  const [estado, setEstado] = useStoredState<EstadoDaPlanilha>(
    `obras.contratos:${organization?.id ?? '-'}:${user?.id ?? '-'}`,
    INICIAL,
    restaurar,
  );
  const [busca, setBusca] = useState('');
  const { data, isPending, isError, error, refetch, isFetching } = useContratos(estado.escopo);

  const termo = normalizarBusca(busca.trim());
  const buscados = useMemo(
    () =>
      termo === ''
        ? (data?.items ?? [])
        : (data?.items ?? []).filter((contrato) => textoDeBusca(contrato).includes(termo)),
    [data, termo],
  );
  // A contagem das abas acompanha a busca: "3 vencidos" com o termo digitado.
  const contagem = useMemo(() => contarPorSituacao(buscados), [buscados]);
  const visiveis = useMemo(
    () =>
      ordenar(
        estado.situacao === 'todos'
          ? buscados
          : buscados.filter((contrato) => contrato.situacao === estado.situacao),
        estado.ordem,
      ),
    [buscados, estado.situacao, estado.ordem],
  );

  const mudar = (parcial: Partial<EstadoDaPlanilha>) =>
    setEstado((atual) => ({ ...atual, ...parcial }));

  function ordenarPor(coluna: ColunaDaPlanilha): void {
    mudar({
      ordem:
        estado.ordem.coluna === coluna
          ? { coluna, sentido: estado.ordem.sentido === 'asc' ? 'desc' : 'asc' }
          : { coluna, sentido: SENTIDO_INICIAL[coluna] },
    });
  }

  function conteudo() {
    if (isError) {
      return (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      );
    }

    if (isPending) return <PlanilhaDeContratosEsqueleto />;

    // Escopo vazio é outra coisa de busca vazia: um pede o órgão, a outra, ajuste.
    if (data.items.length === 0) {
      return (
        <Card square>
          {estado.escopo === 'minhas' ? (
            <EmptyState
              icon={ScrollText}
              title="Nenhum contrato nas suas obras"
              description="Aparecem aqui os contratos das obras que você cadastrou, pelas quais responde ou em cuja equipe está."
              action={
                <Button
                  icon={<Building2 size={15} strokeWidth={1.5} />}
                  onClick={() => mudar({ escopo: 'orgao' })}
                >
                  Ver todo o órgão
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={ScrollText}
              title="Nenhum contrato no órgão"
              description="Os contratos vêm do cadastro das obras: assim que uma obra for cadastrada, o contrato dela aparece aqui."
            />
          )}
        </Card>
      );
    }

    if (visiveis.length === 0) {
      return (
        <Card square>
          <EmptyState
            icon={SearchX}
            title="Nenhum contrato encontrado"
            description={
              termo !== ''
                ? `Nada corresponde a “${busca.trim()}”${estado.situacao === 'todos' ? '' : ` em ${ABA[estado.situacao].toLowerCase()}`}. A busca olha o número do contrato, a obra, a contratada, o CNPJ e o fiscal.`
                : 'Não há contratos nesta situação.'
            }
            action={
              termo !== '' ? (
                <Button onClick={() => setBusca('')}>Limpar busca</Button>
              ) : (
                <Button onClick={() => mudar({ situacao: 'todos' })}>Ver todos</Button>
              )
            }
          />
        </Card>
      );
    }

    return (
      <PlanilhaDeContratos
        contratos={visiveis}
        ordem={estado.ordem}
        onOrdenar={ordenarPor}
        chave={`${estado.escopo}-${estado.situacao}-${estado.ordem.coluna}-${estado.ordem.sentido}`}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Contratos"
        description="O acompanhamento dos contratos das obras: vigência, prazo e saldo. Tudo vem do cadastro das obras e dos relatórios — para corrigir um contrato, edite a obra."
      />

      {/*
       * A barra: de quais obras (as minhas, de saída, ou o órgão inteiro) e a
       * busca, que ocupa o que sobra. No celular, a busca desce para uma linha
       * só dela.
       */}
      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          label="Contratos de quais obras"
          options={ESCOPOS}
          value={estado.escopo}
          onChange={(escopo) => mudar({ escopo })}
        />
        <div className="min-w-0 flex-1 basis-72">
          <SearchInput
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            onClear={() => setBusca('')}
            placeholder="Buscar por contrato, obra, contratada ou fiscal"
            aria-label="Buscar nos contratos"
          />
        </div>
      </div>

      {/* As situações, na ordem de urgência, com quantos contratos há em cada uma. */}
      <div className="mt-4">
        <TabList label="Situação do contrato">
          <Tab
            active={estado.situacao === 'todos'}
            onClick={() => mudar({ situacao: 'todos' })}
            icon={Files}
            count={data ? buscados.length : undefined}
          >
            Todos
          </Tab>
          {SITUACOES_DE_CONTRATO.map((situacao) => (
            <Tab
              key={situacao}
              active={estado.situacao === situacao}
              onClick={() => mudar({ situacao })}
              icon={APARENCIA_DA_SITUACAO[situacao].icone}
              count={data ? contagem[situacao] : undefined}
              disabled={Boolean(data) && contagem[situacao] === 0 && estado.situacao !== situacao}
            >
              {ABA[situacao]}
            </Tab>
          ))}
        </TabList>
      </div>

      {/* Revalidação não desmonta a planilha: só sinaliza com a barra fina. */}
      <div className="my-4">
        <RefreshingBar active={isFetching && !isPending} />
      </div>

      {conteudo()}
    </>
  );
}
